import Iconify from "../../../components/Iconify";
import MenuPopover from "../../../components/MenuPopover";
import Label from "src/components/Label";
import { Fragment, useEffect, useState } from "react";
import {
    Box,
    List,
    Badge,
    Tooltip,
    Divider,
    IconButton,
    Typography,
    ListItemText,
    ListItemAvatar,
    ListItem,
    Stack,
} from "@mui/material";
import { IconButtonAnimate } from "../../../components/animate";
import { AlertIcon, CompanyIcon, InvoicesDownloadIcon } from "src/theme/overrides/CustomIcons";
import { useTranslation } from "react-i18next";
import { WSS, WSSMessage } from "src/utils/wss";
import { WssSessionEventTypes } from "src/modules/session/types/Wss";
import { WssStationEventTypes } from "src/modules/station/types/Wss";
import { getStationEvseStatusColor, getStationStatusColor } from "src/modules/station/utils";
import {
    StationEvseAvailabilityStatus,
    StationEvseStatus,
    StationStatus,
} from "src/modules/station/types/Station";
import CirclePoint from "src/components/CirclePoint";
import { WssInvoiceEventTypes } from "src/modules/invoice/types/Wss";
import { WssUserEventTypes } from "src/modules/user/types/Wss";
import { WssActionTypes, useWssDispatch, useWssState } from "src/contexts/WSSContext";
import { useNavigate } from "react-router";
import {
    PATH_CHOOSE_ORGANISATION,
    PATH_SALES_SESSIONS,
    PATH_STATION_DETAILS,
} from "src/routes/paths";
import { useUserInvitationList } from "src/modules/user/hooks/UserInvitation/useUserInvitationList";
import { axiosCompanyInstance, axiosCoreInstance } from "src/config/axios";
import FileSaver from "file-saver";
import useAuth from "src/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { useCompanyList } from "src/modules/company/hooks/useCompanyList";
import { RQ_USER_PROFILE_PERMISSIONS_KEY } from "src/modules/profile/hooks/Profile/useUserPermissions";
import { Storage } from "src/utils/storage";
import { UserActionTypes, useUserDispatch } from "src/contexts/UserContext";
import { CompanyListModel } from "src/modules/company/types/Company";

type NotificationItemProps = {
    variant: WssSessionEventTypes | WssStationEventTypes | WssInvoiceEventTypes | WssUserEventTypes;
    message: WSSMessage;
    switchCompanyContext: (companyId?: string) => Promise<boolean>;
};

const NotificationItem = ({ variant, message, switchCompanyContext }: NotificationItemProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { refetch: refetchInvitations } = useUserInvitationList();

    const runInNotificationCompany = async (action: () => void | Promise<void>) => {
        const companyChanged = await switchCompanyContext(message.payload?.companyId);

        if (!companyChanged) {
            return;
        }

        await action();
    };

    return (
        <ListItem
            sx={{
                py: 2,
                px: 2.5,
                flexDirection:
                    variant === WssInvoiceEventTypes.PDF_CREATED ||
                    variant === WssUserEventTypes.COMPANY_INVITATION
                        ? undefined
                        : "column",
                alignItems:
                    variant === WssInvoiceEventTypes.PDF_CREATED ||
                    variant === WssUserEventTypes.COMPANY_INVITATION
                        ? undefined
                        : "flex-start",
            }}
        >
            {variant === WssStationEventTypes.STATION_STATUS && (
                <>
                    <Label
                        variant="ghost"
                        color={getStationStatusColor(message.payload.status as StationStatus)}
                    >
                        {t(message.payload.status)}
                    </Label>
                    <Stack
                        direction="row"
                        sx={{
                            cursor: "pointer",
                            mt: 1,
                            width: "1",
                        }}
                    >
                        <Box
                            sx={{ flex: 1 }}
                            onClick={
                                message.payload.stationId
                                    ? () => {
                                          runInNotificationCompany(() => {
                                              navigate(
                                                  PATH_STATION_DETAILS.replace(
                                                      ":id",
                                                      message.payload.stationId
                                                  )
                                              );
                                          });
                                      }
                                    : undefined
                            }
                        >
                            <Box>
                                <Typography variant="tableOverline">
                                    {t("shared.layout.notifications.station")}
                                </Typography>
                                <Typography variant="body2">
                                    {message.payload.stationIdentifier}
                                </Typography>
                            </Box>
                            {message.payload.locationName && (
                                <Box>
                                    <Typography variant="tableOverline">
                                        {t("shared.layout.notifications.location")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {message.payload.locationName}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </>
            )}
            {variant === WssStationEventTypes.EVSE_STATUS && (
                <>
                    <Label
                        variant="ghost"
                        color={getStationEvseStatusColor(
                            message.payload.status as StationEvseStatus
                        )}
                    >
                        {t(message.payload.status)}
                    </Label>
                    <Stack
                        direction="row"
                        sx={{
                            mt: 1,
                            width: "1",
                        }}
                    >
                        <Box
                            sx={{ flex: 1 }}
                            onClick={
                                message.payload.stationId
                                    ? () => {
                                          runInNotificationCompany(() => {
                                              navigate(
                                                  PATH_STATION_DETAILS.replace(
                                                      ":id",
                                                      message.payload.stationId
                                                  )
                                              );
                                          });
                                      }
                                    : undefined
                            }
                        >
                            <Box>
                                <Typography variant="tableOverline">
                                    {t("shared.layout.notifications.station")}
                                </Typography>
                                <Typography variant="body2">
                                    {message.payload.stationIdentifier}
                                </Typography>
                            </Box>
                            {message.payload.locationName && (
                                <Box>
                                    <Typography variant="tableOverline">
                                        {t("shared.layout.notifications.location")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {message.payload.locationName}
                                    </Typography>
                                </Box>
                            )}
                            <Box>
                                <Typography variant="tableOverline">
                                    {t("shared.layout.notifications.connector")}
                                </Typography>
                                <CirclePoint size="small" color="primary">
                                    {`#${message.payload.connectorId}`}
                                </CirclePoint>
                            </Box>
                        </Box>
                    </Stack>
                </>
            )}
            {variant === WssStationEventTypes.EVSE_NOT_FOUND && (
                <>
                    <Label variant="ghost" color="error">
                        {t("shared.layout.notifications.evseNotFound")}
                    </Label>
                    <Stack
                        direction="row"
                        sx={{
                            mt: 1,
                            width: "1",
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Box>
                                <Typography variant="tableOverline">
                                    {t("shared.layout.notifications.station")}
                                </Typography>
                                <Typography variant="body2">
                                    {message.payload.stationIdentifier || message.payload.stationId}
                                </Typography>
                            </Box>
                            {message.payload.locationName && (
                                <Box>
                                    <Typography variant="tableOverline">
                                        {t("shared.layout.notifications.location")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {message.payload.locationName}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </>
            )}
            {variant === WssSessionEventTypes.SESSION_STATUS && (
                <>
                    <Label variant="ghost" color="info">
                        {message.payload.status}
                    </Label>
                    <Stack
                        direction="row"
                        sx={{
                            mt: 1,
                            width: "1",
                        }}
                    >
                        <Box
                            sx={{ flex: 1 }}
                            onClick={() => {
                                runInNotificationCompany(() => {
                                    navigate(PATH_SALES_SESSIONS);
                                });
                            }}
                        >
                            <Box>
                                <Typography variant="tableOverline">
                                    {t("shared.layout.notifications.session")}
                                </Typography>
                                <Typography variant="body2">{message.payload.id}</Typography>
                            </Box>
                        </Box>
                    </Stack>
                </>
            )}

            {variant === WssStationEventTypes.STATION_CONFIG_CHANGE && (
                <>
                    <Label variant="ghost" color="info">
                        {message.payload.status}
                    </Label>
                    <Stack
                        direction="row"
                        sx={{
                            mt: 1,
                            width: "1",
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Box>
                                <Typography variant="tableOverline">
                                    {t("shared.layout.notifications.configChange")}
                                </Typography>
                                <Typography variant="body2">{`${message.payload.key} -> ${message.payload.newValue}`}</Typography>
                            </Box>
                        </Box>
                    </Stack>
                </>
            )}

            {variant === WssStationEventTypes.STATION_CONFIG_REFRESH && (
                <>
                    <Label variant="ghost" color="info">
                        {t("shared.layout.notifications.configRefresh")}
                    </Label>
                    <Stack
                        direction="row"
                        sx={{
                            mt: 1,
                            width: "1",
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Box>
                                <Typography variant="tableOverline">
                                    {t("shared.layout.notifications.station")}
                                </Typography>
                                <Typography variant="body2">
                                    {message.payload.stationIdentifier || message.payload.stationId}
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </>
            )}

            {variant === WssStationEventTypes.STATION_RESET && (
                <>
                    <Label variant="ghost" color={message.payload.success ? "success" : "error"}>
                        {message.payload.success ? t("shared.success") : t("shared.error")}
                    </Label>
                    <Stack
                        direction="row"
                        sx={{
                            mt: 1,
                            width: "1",
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Box>
                                <Typography variant="tableOverline">
                                    {t("shared.layout.notifications.stationResetSuccess")}
                                </Typography>
                                <Typography variant="body2">
                                    {message.payload.stationIdentifier || message.payload.stationId}
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </>
            )}

            {variant === WssStationEventTypes.STATION_AVAILABILITY && (
                <>
                    <Label
                        variant="ghost"
                        color={
                            message.payload.status === StationEvseAvailabilityStatus.REJECTED
                                ? "error"
                                : "success"
                        }
                    >
                        {message.payload.status}
                    </Label>
                    <Stack
                        direction="row"
                        sx={{
                            mt: 1,
                            width: "1",
                            cursor: "pointer",
                        }}
                    >
                        <Box
                            sx={{ flex: 1 }}
                            onClick={() => {
                                runInNotificationCompany(() => {
                                    navigate(
                                        PATH_STATION_DETAILS.replace(
                                            ":id",
                                            message.payload.stationId
                                        )
                                    );
                                });
                            }}
                        >
                            <Box>
                                <Typography variant="tableOverline">
                                    {t("shared.layout.notifications.stationStatusAccepted")}
                                </Typography>
                                <Typography variant="body2">
                                    {message.payload.stationIdentifier || message.payload.stationId}
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </>
            )}

            {variant === WssStationEventTypes.EVSE_AVAILABILITY && (
                <>
                    <Label
                        variant="ghost"
                        color={
                            message.payload.status === StationEvseAvailabilityStatus.REJECTED
                                ? "error"
                                : "success"
                        }
                    >
                        {message.payload.status}
                    </Label>
                    <Stack
                        direction="row"
                        sx={{
                            mt: 1,
                            width: "1",
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Box>
                                <Typography variant="tableOverline">
                                    {t("shared.layout.notifications.stationEvseStatusAccepted")}
                                </Typography>
                                <Typography variant="body2">
                                    {message.payload.stationIdentifier || message.payload.stationId}
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </>
            )}

            {variant === WssInvoiceEventTypes.PDF_CREATED && (
                <>
                    <ListItemAvatar sx={{ color: "text.secondary", lineHeight: 1 }}>
                        <InvoicesDownloadIcon fontSize="small" color="inherit" />
                    </ListItemAvatar>
                    <ListItemText
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                            runInNotificationCompany(async () => {
                                const res = await axiosCompanyInstance.get(
                                    `invoice/${
                                        message.payload?.class === "ChargingInvoice"
                                            ? "charging"
                                            : message.payload?.class === "CommissionInvoice"
                                              ? "commission"
                                              : "fee"
                                    }/${message.payload?.id}/download`,
                                    {
                                        responseType: "blob",
                                    }
                                );

                                FileSaver.saveAs(
                                    res.data,
                                    `${
                                        message.payload?.invoiceNumber || message.payload?.id // Mateusz ma to TODO
                                    }.pdf`
                                );
                            });
                        }}
                        primary={
                            <>
                                {t("shared.layout.notifications.pdfCreated1")}{" "}
                                <Typography variant="body2" component="span" color="text.secondary">
                                    {message.payload?.label}
                                </Typography>{" "}
                                {t("shared.layout.notifications.pdfCreated2")}
                            </>
                        }
                        primaryTypographyProps={{
                            fontSize: 14,
                        }}
                    />
                </>
            )}

            {variant === WssUserEventTypes.COMPANY_INVITATION && (
                <>
                    <ListItemAvatar sx={{ color: "text.secondary" }}>
                        <CompanyIcon fontSize="small" color="inherit" />
                    </ListItemAvatar>
                    <ListItemText
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                            refetchInvitations();
                            navigate(PATH_CHOOSE_ORGANISATION);
                        }}
                        primary={
                            <>
                                {t("shared.layout.notifications.youHaveBeenInvited")}{" "}
                                <Typography variant="body2" component="span" color="text.secondary">
                                    {message.payload.companyName}
                                </Typography>
                            </>
                        }
                        primaryTypographyProps={{
                            fontSize: 14,
                        }}
                    />
                </>
            )}
        </ListItem>
    );
};

const NotificationsPopover = () => {
    const { t } = useTranslation();
    const wssState = useWssState();
    const [wssMessages, setWssMessages] = useState<WSSMessage[]>([]);
    const queryClient = useQueryClient();
    const { userState } = useAuth();
    const userDispatch = useUserDispatch();
    const { data: companies, refetch: refetchCompanies } = useCompanyList();

    const dispatch = useWssDispatch();

    const switchCompanyContext = async (companyId?: string) => {
        if (!companyId || userState?.company?.id === companyId) {
            return true;
        }

        let availableCompanies: CompanyListModel[] = companies || [];

        if (!availableCompanies.length) {
            const companiesResponse = await refetchCompanies();
            availableCompanies = companiesResponse.data || [];
        }

        const targetCompany = availableCompanies.find(
            (company: CompanyListModel) => company.id === companyId
        );

        if (!targetCompany) {
            return false;
        }

        const permissionsResponse = await axiosCoreInstance.get(
            `api/user/permissions/${companyId}`
        );
        const permissions = permissionsResponse?.data || null;

        Storage.setItem("currentCompany", targetCompany);
        userDispatch({ type: UserActionTypes.SET_COMPANY, payload: { ...targetCompany } });
        Storage.setItem("userRoles", permissions);
        userDispatch({ type: UserActionTypes.SET_ROLES, payload: { ...permissions } });
        queryClient.setQueryData([RQ_USER_PROFILE_PERMISSIONS_KEY, companyId], permissions);

        return true;
    };

    //setSeenAll
    const setAllSeen = () => {
        WSS.setAllSeen();
        dispatch({ type: WssActionTypes.SET_ALL_SEEN });
    };

    //setSeenAll

    useEffect(() => {
        setWssMessages(wssState?.lastMessages ? wssState?.lastMessages : []);
    }, [wssState]);

    // STATION_STATUS = "station_status",
    // EVSE_STATUS = "evse_status",
    // EVSE_NOT_FOUND = "evse_not_found",
    // STATION_CONFIG_CHANGE = "station_config_change",
    // STATION_CONFIG_REFRESH = "station_config_refresh",
    // STATION_RESET = "station_reset",
    // STATION_AVAILABILITY = "station_availability",
    // EVSE_AVAILABILITY = "evse_availability",
    // UNLOCK_CONNECTOR = "unlock_connector",

    const shouldShowMessageOnPopover = (message: WSSMessage) => {
        const { payload } = message;
        if (payload.event === WssUserEventTypes.COMPANY_INVITATION) {
            return true;
        }
        if (
            payload.event === WssStationEventTypes.STATION_STATUS &&
            ["error", "reboot-required", "offline"].includes(message.payload?.status || "")
        ) {
            return true;
        }

        if (
            payload.event === WssStationEventTypes.EVSE_STATUS &&
            ["error"].includes(message.payload?.status || "")
        ) {
            return true;
        }

        if (payload.event === WssStationEventTypes.EVSE_NOT_FOUND) {
            return true;
        }

        if (
            payload.event === WssStationEventTypes.STATION_CONFIG_CHANGE &&
            ["Rejected", "RebootRequired", "NotSupported"].includes(payload.status)
        ) {
            return true;
        }
        if (
            payload.event === WssStationEventTypes.STATION_AVAILABILITY &&
            ["Rejected"].includes(payload.status)
        ) {
            return true;
        }
        if (
            payload.event === WssStationEventTypes.EVSE_AVAILABILITY &&
            ["Rejected"].includes(payload.status)
        ) {
            return true;
        }
        if (payload.event === WssInvoiceEventTypes.PDF_CREATED) {
            return true;
        }

        return false;
    };

    const totalUnRead = wssMessages
        .filter((wm) => wm.status === "new")
        .filter((wm) => shouldShowMessageOnPopover(wm)).length;

    const [open, setOpen] = useState<HTMLElement | null>(null);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setOpen(event.currentTarget);
    };

    const handleClose = () => {
        setOpen(null);
        setAllSeen();
    };

    const handleMarkAllAsRead = () => {
        dispatch({ type: WssActionTypes.SET_MESSAGES, payload: [] });
        WSS.clearMessages();
        setWssMessages([]);
    };

    return (
        <>
            <IconButtonAnimate onClick={handleOpen} sx={{ width: 40, height: 40 }}>
                <Badge badgeContent={totalUnRead} color="error">
                    <AlertIcon fontSize="small" color="inherit" />
                </Badge>
            </IconButtonAnimate>

            <MenuPopover
                open={Boolean(open)}
                anchorEl={open}
                onClose={handleClose}
                sx={{ width: 360, p: 0, mt: 1.5, ml: 0.75 }}
            >
                <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1">
                            {t("shared.layout.notifications.title")}
                        </Typography>
                    </Box>

                    {wssMessages.filter((wm) => shouldShowMessageOnPopover(wm)).length > 0 && (
                        <Tooltip title={t("shared.layout.notifications.markAllAsRead")}>
                            <IconButton color="success" onClick={handleMarkAllAsRead}>
                                <Iconify icon="eva:done-all-fill" width={20} height={20} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Divider variant="solid" />
                <List disablePadding>
                    {wssMessages?.filter((lastMessage: WSSMessage) =>
                        shouldShowMessageOnPopover(lastMessage)
                    ).length > 0 ? (
                        wssMessages
                            ?.filter((lastMessage: WSSMessage) =>
                                shouldShowMessageOnPopover(lastMessage)
                            )
                            .map((lastMessage: WSSMessage) => (
                                <Fragment key={lastMessage.timestamp}>
                                    <NotificationItem
                                        message={lastMessage}
                                        variant={lastMessage.payload?.event}
                                        switchCompanyContext={switchCompanyContext}
                                    />
                                    <Divider variant="solid" />
                                </Fragment>
                            ))
                    ) : (
                        <Box sx={{ padding: 2 }}>
                            <i>{t("shared.layout.notifications.noNotifications")}</i>
                        </Box>
                    )}
                </List>
            </MenuPopover>
        </>
    );
};

export default NotificationsPopover;
